import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, Rows3 } from 'lucide-react';
import { AppTile } from '../../components/AppTile';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { apiSend } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import type { AppRecord } from '../../lib/types';

type HomeView = 'all' | 'by-category';

interface AllAppsGridProps {
  apps: AppRecord[];
  appsByCategory: Array<{ category: string; apps: AppRecord[] }>;
  pinnedSet: Set<string>;
  busyId: string | null;
  loading: boolean;
  error: unknown;
  readOnly: boolean;
  homeView: HomeView;
  onChangeHomeView: (next: HomeView) => void;
  onTogglePin: (app: AppRecord) => void;
  onRequestAccess: (app: AppRecord | null) => void;
}

// Classic-variant block: the full apps grid with category toggle. Kept as a
// separate component so Cozy/Focused can opt OUT and only show "Browse all →".

export const AllAppsGrid = ({
  apps,
  appsByCategory,
  pinnedSet,
  busyId,
  loading,
  error,
  readOnly,
  homeView,
  onChangeHomeView,
  onTogglePin,
  onRequestAccess,
}: AllAppsGridProps) => {
  const { getToken } = useAuth();

  const persistHomeView = useCallback(
    async (next: HomeView) => {
      if (next === homeView) return;
      onChangeHomeView(next);
      if (readOnly) return;
      try {
        localStorage.setItem('onedesk.homeView', next);
      } catch {
        /* ignore */
      }
      try {
        const token = await getToken();
        await apiSend('PATCH', '/api/me/preferences', { homeView: next }, token);
      } catch {
        // Local state authoritative
      }
    },
    [homeView, onChangeHomeView, readOnly, getToken],
  );

  return (
    <section aria-labelledby="apps-heading">
      <div className="mb-3 flex items-center justify-between">
        <h2 id="apps-heading" className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
          All apps
        </h2>
        <div className="flex items-center gap-3">
          <div
            role="radiogroup"
            aria-label="View"
            className="inline-flex h-8 overflow-hidden rounded-md border border-line"
          >
            <button
              type="button"
              role="radio"
              aria-checked={homeView === 'all'}
              onClick={() => void persistHomeView('all')}
              title="Flat grid"
              className={cn(
                'grid w-9 place-items-center text-text-secondary transition-colors',
                homeView === 'all' ? 'bg-accent text-accent-contrast' : 'hover:bg-bg-secondary',
              )}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={homeView === 'by-category'}
              onClick={() => void persistHomeView('by-category')}
              title="Group by category"
              className={cn(
                'grid w-9 place-items-center text-text-secondary transition-colors',
                homeView === 'by-category'
                  ? 'bg-accent text-accent-contrast'
                  : 'hover:bg-bg-secondary',
              )}
            >
              <Rows3 className="h-4 w-4" />
            </button>
          </div>
          <Link to="/apps" className="text-xs text-accent hover:underline">
            Browse all →
          </Link>
        </div>
      </div>
      {loading ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-status-critical/30 bg-status-critical/10 p-4 text-sm text-status-critical">
          Couldn't load apps. Try refreshing.
        </div>
      ) : homeView === 'by-category' ? (
        <div className="space-y-6">
          {appsByCategory.map(({ category: cat, apps: catApps }) => (
            <div key={cat}>
              <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                {cat}
              </h3>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {catApps.map((app) => (
                  <AppTile
                    key={app.id}
                    app={app}
                    pinned={pinnedSet.has(app.id)}
                    onTogglePin={onTogglePin}
                    onRequestAccess={onRequestAccess}
                    busy={busyId === app.id}
                    preview={readOnly}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {apps.slice(0, 9).map((app) => (
            <AppTile
              key={app.id}
              app={app}
              pinned={pinnedSet.has(app.id)}
              onTogglePin={onTogglePin}
              onRequestAccess={onRequestAccess}
              busy={busyId === app.id}
              preview={readOnly}
            />
          ))}
        </div>
      )}
    </section>
  );
};
